import { FileActions, fileModule } from "@/store/modules/file.module"

const MEDIA_ERR_ABORTED = 1

const MAX_REFRESHES = 3

const attached = new WeakSet<HTMLVideoElement>()

function waitForMetadata(video: HTMLVideoElement): Promise<void> {
  if (video.readyState >= HTMLMediaElement.HAVE_METADATA) return Promise.resolve()

  return new Promise(resolve => {
    const done = () => {
      video.removeEventListener("loadedmetadata", done)
      video.removeEventListener("error", done)
      resolve()
    }
    video.addEventListener("loadedmetadata", done)
    video.addEventListener("error", done)
  })
}

export function attachVideoStreamRecovery(
  video: HTMLVideoElement,
  getUrl: () => Promise<string>
): () => void {
  if (attached.has(video)) return () => undefined
  attached.add(video)

  let refreshes = 0
  let refreshing = false

  const onError = async () => {
    if (refreshing || refreshes >= MAX_REFRESHES) return
    if (video.error?.code === MEDIA_ERR_ABORTED) return

    refreshing = true
    refreshes += 1

    const position = video.currentTime
    const wasPlaying = !video.paused && !video.ended

    try {
      const url = await getUrl()
      if (!url || url === video.currentSrc) return

      video.src = url
      video.load()
      await waitForMetadata(video)

      if (position > 0) {
        try {
          video.currentTime = position
        } catch {
          // Seeking is not always possible; playback then simply restarts from the beginning.
        }
      }

      if (wasPlaying) await video.play().catch(() => undefined)
    } finally {
      refreshing = false
    }
  }

  video.addEventListener("error", onError)

  return () => {
    video.removeEventListener("error", onError)
    attached.delete(video)
  }
}

export function getSlideFileMetadataId(slide) {
  return slide.fileMetadataId || slide.$trigger?.dataset?.fileMetadataId || null
}

export function attachSlideStreamRecovery(slide) {
  const fileMetadataId = getSlideFileMetadataId(slide)
  const content = slide.$content
  if (!fileMetadataId || !content) return

  const video =
    content.tagName === "VIDEO" ? content : content.querySelector && content.querySelector("video")
  if (!video) return

  const projectId = slide.projectId || slide.$trigger?.dataset?.projectId || undefined

  attachVideoStreamRecovery(video, () =>
    fileModule[FileActions.ATTACHMENT_VIDEO_URL]({
      projectId,
      attachmentId: fileMetadataId,
      force: true
    })
  )
}
